/* eslint-disable no-unused-vars */
import './App.css';
import './index.css';
import './candidate.css';
import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/authContext';
import AuthkeyCheckProvider from './context/authkeyCheck';
import { UnreadCountCheckProvider } from './context/unreadCountContext';
import { CompanyWebSocketProvider } from './views/company/socket/CompanyWebSocketProvider';
import { CandidateWebSocketProvider } from './views/candidate/socket/CandidateWebSocketProvider';
import ProtectedRoute from './ProtectedRoute';
import PublicRoute from './PublicRoute';
import { ScoutProvider } from './views/company/scoutmanagement/ScoutContext';
import { SecretEntryProvider } from './views/company/secretentry/ScoutTemplate/SecretEntryContext';
import AppLayout from './AppLayout';
import CircularProgress from '@mui/material/CircularProgress';
import CompanyRegistrationGuard from './company-components/CompanyRegistrationGuard';
import CompanyStepOneRegistrationGuard from './company-components/CompanyStepOneRegistrationGuard';
import CompanyStepTwoRegistrationGuard from './company-components/CompanyStepTwoRegistrationGuard';
import AgentApprovalPendingModal from './company-components/AgentApprovalPendingModal';
import ServiceSuspended from './views/ServiceSuspended/ServiceSuspended';

const IS_SERVICE_SUSPENDED = true;

// =======================
//  Lazy Imports (TOP LEVEL)
// =======================

// Candidate side imports
const CandidateRegistrationForm = lazy(() => import('./views/candidate/RegistrationForm'));
const CandidateLoginForm = lazy(() => import('./views/candidate/LoginForm'));
const CandidateForgetPassword = lazy(() => import('./views/candidate/ForgetPassword'));
const CandidateFPAuthentication = lazy(() => import('./views/candidate/fp-authentication'));
const CandidateRegistrationStepper = lazy(() => import('./views/candidate/registration'));
const CandidateRegistrationAuth = lazy(() => import('./views/candidate/registrationauth'));
const CandidateResetPasswordPage = lazy(() => import('./views/candidate/ResetPasswordPage'));
const CandidateTerms = lazy(() => import('./views/candidate/terms'));
const CandidatePrivacy = lazy(() => import('./views/candidate/privacy'));
const CandidateMyPage = lazy(() => import('./views/candidate/mypage'));
const CandidateMessageComponent = lazy(() => import('./views/candidate/message'));
const CandidateInProgressComponent = lazy(() => import('./views/candidate/inprogress'));
const CandidateChangePassword = lazy(() => import('./views/candidate/ChangePassword'));
const CandidateNormalResume = lazy(() => import('./views/candidate/normalresume'));
const CandidateProfileComponent = lazy(() => import('./views/candidate/profile'));
const CandidateRecruitmentFullInfo = lazy(() => import('./views/candidate/RecruitmentFullInfo'));
const CandidateSecretEntryFullInfo = lazy(() => import('./views/candidate/SecretEntryFullInfo'));
const CandidateCorporateFull = lazy(() => import('./views/candidate/CorporateScoutFull'));
const CandidateCorporateScoutFullInfo = lazy(() => import('./views/candidate/CorporateScoutFullInfo'));
const CandidateOccupationComponent = lazy(() => import('./candidate-components/occupation'));
const CandidateIndustryComponent = lazy(() => import('./candidate-components/industry'));
const CandidtaeIncomeComponent = lazy(() => import('./candidate-components/income'));
const CandidateLocationComponent = lazy(() => import('./candidate-components/location'));
const CandidateAccountingSkill = lazy(() => import('./candidate-components/AccountingSkill'));
const CandidateFullProgress = lazy(() => import('./views/candidate/FullProgress'));
const CandidateInprogressDetail = lazy(() => import('./views/candidate/InprogressDetail'));
const CandidateMaskingResume = lazy(() => import('./views/candidate/MaskingResume'));
const CandidateRecommendedJobSettings = lazy(() => import('./views/candidate/RecommendedJobSettings'));
const CandidateRecommendedJobForm = lazy(() => import('./views/candidate/RecommendedJobForm'));
const CandidateEmailDeliverySettings = lazy(() => import('./views/candidate/EmailDeliverySettings'));
const CandidateInquiry = lazy(() => import('./views/candidate/Inquiry'));
const CandidateChangeMemberID = lazy(() => import('./views/candidate/ChangeMemberID'));
const CandidateChangeMobileNumber = lazy(() => import('./views/candidate/ChangeMobileNumber'));
const CandidateChangeMemberIDVerify = lazy(() => import('./views/candidate/ChangeMemberIDVerify'));
const CandidateMaskingApplication = lazy(() => import('./views/candidate/MaskingApplication'));
const CandidateMaskingApplicationConfirm = lazy(() => import('./views/candidate/MaskingApplicationConfirm'));
const CandidateAnonymousProfile = lazy(() => import('./views/candidate/AnonymousProfile'));
const CandidateNormalApplication = lazy(() => import('./views/candidate/NormalApplication'));
const CandidateNormalApplicationConfirm = lazy(() => import('./views/candidate/NormalApplicationConfirm'));
const CandidateDocumentScreeningEntry = lazy(() => import('./views/candidate/DocumentScreeningEntry'));
const CandidateDocumentScreeningConfirm = lazy(() => import('./views/candidate/DocScreeningEntryConfirm'));
const CandidateDirectEntry = lazy(() => import('./views/candidate/DirectEntry'));
const CandidateDirectEntryConfirm = lazy(() => import('./views/candidate/DirectEntryConfirm'));
const CandidateSearchInfo = lazy(() => import('./views/candidate/SearchInfo'));
const CandidateWorkHistory = lazy(() => import('./views/candidate/profile/WorkHistory'));
const CandidateMessageTemplate = lazy(() => import('./views/candidate/MessageTemplate'));
const CandidateCreateMessage = lazy(() => import('./views/candidate/CreatemessageTemplate'));
const CandidateEditMessage = lazy(() => import('./views/candidate/EditmessageTemplate'));
const CandidateChatComponent = lazy(() => import('./views/candidate/chat'));
const CandidateCompanyBlock = lazy(() => import('./views/candidate/CompanyBlock'));
const CandidateEditMaskingResume = lazy(() => import('./views/candidate/EditMaskingResume'));
const CandidateSkillentry = lazy(() => import('./views/candidate/registration/skillentry'));
const CandidateSkillComparison = lazy(() => import('./candidate-components/skillcomparison'));
const CandidateSearchFullInfo = lazy(() => import('./views/candidate/SearchFullInfo'));
const CandidateMailIdChangeAuth = lazy(() => import('./views/candidate/mailIDChangeAuth'));
const CandidateNotification = lazy(() => import('./views/candidate/Notification'));
const CandidateNotificationDetail = lazy(() => import('./views/candidate/NotificationDetail'));
const CandidateCorporateBlock = lazy(() => import('./views/candidate/CorporateBlock'));
const CandidateAgentPage = lazy(() => import('./views/candidate/AgentPage'));
const CandidateAgentConsultationLog = lazy(() => import('./views/candidate/AgentLog'));
const CandidateAgentLogInfo = lazy(() => import('./views/candidate/AgentLogInfo'));
const CandidateFirstInterviewEntry = lazy(() => import('./views/candidate/FirstInterviewEntry'));
const CandidateFirstInterviewConfirm = lazy(() => import('./views/candidate/FirstInterviewEntryConfirm'));

// Company side imports
const Login = lazy(() => import('./views/company/auth/LoginPage'));
const ForgotPassword = lazy(() => import('./views/company/auth/forgetPassword'));
const ForgotPasswordMail = lazy(() => import('./views/company/auth/forgetPasswordMail'));
const NewPassword = lazy(() => import('./views/company/auth/newPassword'));
const RegistrationHome = lazy(() => import('./views/company/registration/registrationHome'));
const RegistrationForm = lazy(() => import('./views/company/registration/registrationForm'));
const RegistrationFormOne = lazy(() => import('./views/company/registration/RegistrationFormOne'));
const RegistrationFormTwo = lazy(() => import('./views/company/registration/RegistrationFormTwo'));
const RegistrationInitialForm = lazy(() => import('./views/company/registration/registrationInitialForm'));
const RegistrationStepOne = lazy(() => import('./views/company/registration/registrationStepOne'));
const RegistrationStepTwo = lazy(() => import('./views/company/registration/registrationStepTwo'));
const JobInfoManagement = lazy(() => import('./views/company/jobinformation/jobinfomanagement'));
const JobRegistration = lazy(() => import('./views/company/jobinformation/jobregistration'));
const DeletedJobsPage = lazy(() => import('./views/company/jobinformation/DeletedJobsPage'));
const MatchingSettings = lazy(() => import('./views/company/matchingsettings/index'));
const SavedConditions = lazy(() => import('./views/company/matchingsettings/savedconditions/index'));
const ScoutManagement = lazy(() => import('./views/company/scoutmanagement/index'));
const SentRemindersPage = lazy(() => import('./views/company/scoutmanagement/SentRemindersPage'));
const SecretEntry = lazy(() => import('./views/company/secretentry/index'));
const InProgress = lazy(() => import('./views/company/inprogress/index'));
const JobSelection = lazy(() => import('./views/company/jobinformation/jobSelection'));
const Settings = lazy(() => import('./views/company/settings/index'));
const Dashboard = lazy(() => import('./views/company/dashboard/index'));
const ScoutTemplates = lazy(() => import('./views/company/scoutmanagement/ScoutTemplate/index'));
const CreateTemplate = lazy(() => import('./views/company/scoutmanagement/ScoutTemplate/CreateTemplate'));
const ScoutSecretTemplates = lazy(() => import('./views/company/secretentry/ScoutTemplate/index'));
const Terms = lazy(() => import('./views/company/auth/Terms'));
const Privacy = lazy(() => import('./views/company/auth/privacy'));
const ChatRoom = lazy(() => import('./views/company/chat/chat'));
const OrderComplete = lazy(() => import('./views/company/order/complete'));

// Admin Auth
const AdminLogin = lazy(() => import('./views/admin/auth/admin-login'));
const AdminForgotPassword = lazy(() => import('./views/admin/auth/admin-forget-password'));
const AdminForgotPasswordMail = lazy(() => import('./views/admin/auth/admin-forget-password-mail'));
const AdminResetPassword = lazy(() => import('./views/admin/auth/admin-reset-password'));

// Admin Dashboard
const AdminDashboard = lazy(() => import('./views/admin/dashboard'));

// Admin - Agent
const AgentList = lazy(() => import('./views/admin/agent/agent-list'));
const AgentDetails = lazy(() => import('./views/admin/agent/agent-detail'));
const AddAgent = lazy(() => import('./views/admin/agent/add-agent'));
const EditAgent = lazy(() => import('./views/admin/agent/edit-agent'));
const SuspendedAgentList = lazy(() => import('./views/admin/agent/suspended-agent-list'));

// Admin - Company
const CompanyProfileList = lazy(() => import('./views/admin/company/company-profile-list'));
const CompanyJobPostList = lazy(() => import('./views/admin/company/company-job-post-list'));
const AdminCompanyJobPostingList = lazy(() => import('./views/admin/company/company-job-posting-list'));
const AdminCompanyJobPostingEdit = lazy(() => import('./views/admin/company/company-job-posting-edit'));
const AdminCompanyDeletedJobs = lazy(() => import('./views/admin/company/company-deleted-jobs'));
const CompanyScoutManagement = lazy(() => import('./views/admin/company/company-scout-management'));
const CompanyContractBillingManagement = lazy(() => import('./views/admin/company/company-contract-billing-management'));
const CompanyContractBillingDetail = lazy(() => import('./views/admin/company/company-contract-billing-detail'));
const CompanyInquiryList = lazy(() => import('./views/admin/company/company-inquiry-list'));
const CompanySearchList = lazy(() => import('./views/admin/company/company-search-list'));
const CompanyProfileDetails = lazy(() => import('./views/admin/company/company-profile-detail'));
const CompanyJobPostDetails = lazy(() => import('./views/admin/company/company-job-post-detail'));
const CompanyScoutLogDetails = lazy(() => import('./views/admin/company/company-scout-log-detail'));
const CompanyScoutLogDetailsResume = lazy(() => import('./views/admin/company/company-scout-log-detail-resume'));
const CompanyWithdrawnList = lazy(() => import('./views/admin/company/company-withdrawn-list'));
const WithdrawnCompanyList = lazy(() => import('./views/admin/company/company-withdrawn-by-agent-list'));
const ScoutGeneratedIssue = lazy(() => import('./views/admin/company/scout-generated-issue'));
const CompanyContractLogDetails = lazy(() => import('./views/admin/company/company-contract-billing-log-detail'));
const CompanyContractGeneratedDetail = lazy(() => import('./views/admin/company/company-contract-generated-detail'));
const CompanyContractLogDetailsResume = lazy(() => import('./views/admin/company/company-contract-log-detail-resume'));

// Admin - Candidate
const CandidateJobConsultation = lazy(() => import('./views/admin/candidate/candidate-job-consultation'));
const CandidateEnquiry = lazy(() => import('./views/admin/candidate/candidate-enquiry'));
const CandidateJobConsultationDetails = lazy(() => import('./views/admin/candidate/candidate-job-consulation-detail'));
const CandidateJobConsultationLog = lazy(() => import('./views/admin/candidate/candidate-job-consultation-log'));
const CandidateEnquiryConversation = lazy(() => import('./views/admin/candidate/candidate-enquiry-conversation'));
const CandidateAgentSearch = lazy(() => import('./views/admin/candidate/candidate-agent-search'));
const CandidateAgentSearchDetails = lazy(() => import('./views/admin/candidate/candidate-agent-search-details'));
const CandidateJobConsultationBeforePersonAssigned = lazy(() => import('./views/admin/candidate/candidate-job-consultation-before-person-assigned'));
const CandidateJobConsultationProceedInterview = lazy(() => import('./views/admin/candidate/candidate-job-consultation-proceed-interview'));

// Admin - Notifications
const NotificationBugsFeatures = lazy(() => import('./views/admin/notification/notifiy-bugs-features'));
const NotificationBugsFeaturesAdd = lazy(() => import('./views/admin/notification/notifiy-bugs-features-add'));
const NotificationAlert = lazy(() => import('./views/admin/notification/notification-alert'));

// =======================
//  AppContent Component
// =======================

function AppContent() {
  const location = useLocation();
  const [isRegistrationGuardOpen, setIsRegistrationGuardOpen] = useState(false);
  const [isStepOneGuardOpen, setIsStepOneGuardOpen] = useState(false);
  const [isStepTwoGuardOpen, setIsStepTwoGuardOpen] = useState(false);
  const [isAgentApprovalPendingOpen, setIsAgentApprovalPendingOpen] = useState(false); 

  // Clean Google Analytics params from URL on mount (global)
  useEffect(() => {
    if (window.location.search) {
      const params = new URLSearchParams(window.location.search);
      params.delete('_gl');
      params.delete('_gcl_au');
      const cleanSearch = params.toString();
      const cleanUrl = window.location.pathname + (cleanSearch ? '?' + cleanSearch : '');
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);

  // Derived flags for rendering modals with correct state
  const isRegisteredRender = localStorage.getItem('company_is_fully_registered') === '1';
  const isJobPublishedRender = localStorage.getItem('initial_job_post_published') === '1';

  // Check path and localStorage on navigation
  React.useEffect(() => {
    const genericGuardPaths = new Set([
        // '/company/dashboard',
        // '/company/jobinfomanagement',
        // '/company/scoutmanagement',
        // '/company/secretentry',
        // '/company/inprogress',
        // '/company/settings',
    ]);

    const stepOneGuardPath = '/company/searchsettings';

    const currentPath = location.pathname;

    const isGenericGuardedPage = genericGuardPaths.has(currentPath);
    const isStepOneGuardedPage = currentPath === stepOneGuardPath;
    const isGuardedPage = isGenericGuardedPage || isStepOneGuardedPage;

    const isRegistered        = localStorage.getItem('company_is_fully_registered') === '1';
    const isJobPublished      = localStorage.getItem('initial_job_post_published') === '1';
    const isAgentApproved     = localStorage.getItem('company_agent_approval_status') === '1';
    const isFirstLogin = localStorage.getItem('first_login') === '1';

    // (0,0) → nothing done yet
    const isRegistrationIncomplete = !isRegistered && !isJobPublished;
    // (1,0) → company registered, job not yet published
    const isStepTwoPending = isRegistered && !isJobPublished;

    // Reset all guards
    setIsRegistrationGuardOpen(false);
    setIsStepOneGuardOpen(false);
    setIsStepTwoGuardOpen(false);
    setIsAgentApprovalPendingOpen(false); // assuming you added this state earlier

    if (!isGuardedPage || (isStepOneGuardedPage && !isFirstLogin)) return; // Skip guard popup for /company/searchsettings if first_login is 0

    // 1) Registered, job NOT published (1,0)
    if (isStepTwoPending) {
        // 1.a) Agent NOT approved → show CompanyStepTwoRegistrationGuard instead of AgentApprovalPendingModal
        if (!isAgentApproved) {
          if (isGuardedPage) {
            setIsStepTwoGuardOpen(true);
          }
          // Block other modals
          return;
        }

        // 1.b) Agent APPROVED (company_is_fully_registered = 1 & company_agent_approval_status = 1 & initial_job_post_published = 0)
        // → open Step Two guard on generic guarded pages AND on /company/searchsettings
        if (isGenericGuardedPage || isStepOneGuardedPage) {
          setIsStepTwoGuardOpen(true);
        }
        return;
    }

    // 2) Registration incomplete (0,0) – original behavior
    if (isRegistrationIncomplete) {
        // STEP ONE guard (searchsettings only)
        if (isStepOneGuardedPage) {
        setIsStepOneGuardOpen(true);
        return;
        }

        // GENERIC guarded pages
        if (isGenericGuardedPage) {
        setIsRegistrationGuardOpen(true);
        }

        return;
    }

    // 3) Registered & job published (1,1) → no modal
    }, [location.pathname]);

  return (
    <>
      <AuthkeyCheckProvider>
        <CompanyWebSocketProvider>
          <CandidateWebSocketProvider>
            <AuthProvider>
              <ScoutProvider>
                <SecretEntryProvider>
                  <UnreadCountCheckProvider>
                    <AppLayout>
                      <div className="App">
                        <Suspense
                          fallback={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',
                                width: '100vw',
                              }}
                            >
                              <CircularProgress />
                            </div>
                          }
                        >
                          <Routes>
                            {/* Candidate routes */}
                            <Route
                              path="/"
                              element={
                                <PublicRoute>
                                  <CandidateRegistrationForm />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/login"
                              element={
                                <PublicRoute>
                                  <CandidateLoginForm />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/forgetpassword"
                              element={
                                <PublicRoute>
                                  <CandidateForgetPassword />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/fpauthentication"
                              element={
                                <PublicRoute>
                                  <CandidateFPAuthentication />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/resetpassword"
                              element={
                                <PublicRoute>
                                  <CandidateResetPasswordPage />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/registrationauth"
                              element={
                                <PublicRoute>
                                  <CandidateRegistrationAuth />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/terms"
                              element={
                                <PublicRoute>
                                  <CandidateTerms />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/privacy"
                              element={
                                <PublicRoute>
                                  <CandidatePrivacy />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/basic-details"
                              element={
                                <ProtectedRoute>
                                  <CandidateRegistrationStepper />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/changememberid-verify"
                              element={
                                <ProtectedRoute>
                                  <CandidateChangeMemberIDVerify />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inprogress"
                              element={
                                <ProtectedRoute>
                                  <CandidateInProgressComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/changepassword"
                              element={
                                <ProtectedRoute>
                                  <CandidateChangePassword />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/normalresume"
                              element={
                                <ProtectedRoute>
                                  <CandidateNormalResume />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/profile"
                              element={
                                <ProtectedRoute>
                                  <CandidateProfileComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/recruitment/:job_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateRecruitmentFullInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/secretentrydetail/:job_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateSecretEntryFullInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/corporatescoutFullInfo/:job_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateCorporateScoutFullInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/occupation"
                              element={
                                <ProtectedRoute>
                                  <CandidateOccupationComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/industry"
                              element={
                                <ProtectedRoute>
                                  <CandidateIndustryComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/income"
                              element={
                                <ProtectedRoute>
                                  <CandidtaeIncomeComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/location"
                              element={
                                <ProtectedRoute>
                                  <CandidateLocationComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/accountingskill"
                              element={
                                <ProtectedRoute>
                                  <CandidateAccountingSkill />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/messages/scout"
                              element={
                                <ProtectedRoute>
                                  <CandidateCorporateFull />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/fullprogress/:job_id/:conversation_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateFullProgress />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inprogressdetail/:job_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateInprogressDetail />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/maskingresume"
                              element={
                                <ProtectedRoute>
                                  <CandidateMaskingResume />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/recommendedjob"
                              element={
                                <ProtectedRoute>
                                  <CandidateRecommendedJobSettings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/recommendedjobform"
                              element={
                                <ProtectedRoute>
                                  <CandidateRecommendedJobForm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/emaildelivery"
                              element={
                                <ProtectedRoute>
                                  <CandidateEmailDeliverySettings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/inquiry"
                              element={
                                <ProtectedRoute>
                                  <CandidateInquiry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/changememberid"
                              element={
                                <ProtectedRoute>
                                  <CandidateChangeMemberID />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/changemobileno"
                              element={
                                <ProtectedRoute>
                                  <CandidateChangeMobileNumber />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/maskingapplication/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateMaskingApplication />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/maskingapplicationconfirm"
                              element={
                                <ProtectedRoute>
                                  <CandidateMaskingApplicationConfirm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/anonymousprofile"
                              element={
                                <ProtectedRoute>
                                  <CandidateAnonymousProfile />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/normalapplication/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateNormalApplication />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/normalapplicationconfirm"
                              element={
                                <ProtectedRoute>
                                  <CandidateNormalApplicationConfirm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/documentscreeningentry/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateDocumentScreeningEntry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/documentscreeningconfirm"
                              element={
                                <ProtectedRoute>
                                  <CandidateDocumentScreeningConfirm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/firstInterview-entry/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateFirstInterviewEntry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/firstInterview-confirm"
                              element={
                                <ProtectedRoute>
                                  <CandidateFirstInterviewConfirm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/direct-entry/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateDirectEntry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/direct-entry-confirm"
                              element={
                                <ProtectedRoute>
                                  <CandidateDirectEntryConfirm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/searchinfo"
                              element={
                                <ProtectedRoute>
                                  <CandidateSearchInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/workhistory"
                              element={
                                <ProtectedRoute>
                                  <CandidateWorkHistory />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/messagetemplate"
                              element={
                                <ProtectedRoute>
                                  <CandidateMessageTemplate />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/createmessage"
                              element={
                                <ProtectedRoute>
                                  <CandidateCreateMessage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/editmessage/:id"
                              element={
                                <ProtectedRoute>
                                  <CandidateEditMessage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/chat/:job_id"
                              element={
                                <ProtectedRoute>
                                  <CandidateChatComponent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/companyblock"
                              element={
                                <ProtectedRoute>
                                  <CandidateCompanyBlock />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/editmaskingresume"
                              element={
                                <ProtectedRoute>
                                  <CandidateEditMaskingResume />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/skillentry"
                              element={
                                <ProtectedRoute>
                                  <CandidateSkillentry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/skillcomparison"
                              element={
                                <ProtectedRoute>
                                  <CandidateSkillComparison />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/searchdetail"
                              element={
                                <ProtectedRoute>
                                  <CandidateSearchFullInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/change-mailid-Auth"
                              element={
                                <ProtectedRoute>
                                  <CandidateMailIdChangeAuth />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/notifications"
                              element={
                                <ProtectedRoute>
                                  <CandidateNotification />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/notifications-details/:notification_uid"
                              element={
                                <ProtectedRoute>
                                  <CandidateNotificationDetail />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/corporate-block"
                              element={
                                <ProtectedRoute>
                                  <CandidateCorporateBlock />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/agent"
                              element={
                                <ProtectedRoute>
                                  <CandidateAgentPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/agent-log"
                              element={
                                <ProtectedRoute>
                                  <CandidateAgentConsultationLog />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/agent-log-information"
                              element={
                                <ProtectedRoute>
                                  <CandidateAgentLogInfo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/privacy-policy"
                              element={
                                <ProtectedRoute>
                                  <CandidatePrivacy />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/terms-conditions"
                              element={
                                <ProtectedRoute>
                                  <CandidateTerms />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/mypage"
                              element={
                                <ProtectedRoute>
                                  <CandidateMyPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/messages"
                              element={
                                <ProtectedRoute>
                                  <CandidateMessageComponent />
                                </ProtectedRoute>
                              }
                            />

                            {/* Company routes */}
                            <Route
                              path="/company"
                              element={
                                <PublicRoute>
                                  <Login />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/login"
                              element={
                                <PublicRoute>
                                  <Login />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/forgotpassword"
                              element={
                                <PublicRoute>
                                  <ForgotPassword />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/forgotpasswordmail"
                              element={
                                <PublicRoute>
                                  <ForgotPasswordMail />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/newpassword"
                              element={
                                <PublicRoute>
                                  <NewPassword />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/registration"
                              element={
                                <PublicRoute>
                                  <RegistrationInitialForm />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/registrationform"
                              element={
                                <PublicRoute>
                                  <RegistrationInitialForm />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/registrationform/1"
                              element={
                                <PublicRoute>
                                  <RegistrationStepOne />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/registrationform/2"
                              element={
                                <PublicRoute>
                                  <RegistrationStepTwo />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/terms"
                              element={
                                <PublicRoute>
                                  <Terms />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/company/privacy"
                              element={
                                <PublicRoute>
                                  <Privacy />
                                </PublicRoute>
                              }
                            />

                            <Route
                              path="/company/dashboard"
                              element={
                                <ProtectedRoute>
                                  <Dashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/jobinfomanagement"
                              element={
                                <ProtectedRoute>
                                  <JobInfoManagement />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/finalregistration"
                              element={
                                <ProtectedRoute>
                                  <RegistrationForm />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/finalregistration/1"
                              element={
                                <ProtectedRoute>
                                  <RegistrationFormOne />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/finalregistration/2"
                              element={
                                <ProtectedRoute>
                                  <RegistrationFormTwo />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/jobinfomanagement/deletedjobs"
                              element={
                                <ProtectedRoute>
                                  <DeletedJobsPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/jobselection"
                              element={
                                <ProtectedRoute>
                                  <JobSelection />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/newjobregistration"
                              element={
                                <ProtectedRoute>
                                  <JobRegistration />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/jobregistration"
                              element={
                                <ProtectedRoute>
                                  <JobRegistration />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/jobregistration/jobedit/:jobPostId"
                              element={
                                <ProtectedRoute>
                                  <JobRegistration />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/searchsettings"
                              element={
                                <ProtectedRoute>
                                  <MatchingSettings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/searchsettings/savedconditions"
                              element={
                                <ProtectedRoute>
                                  <SavedConditions />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/scoutmanagement"
                              element={
                                <ProtectedRoute>
                                  <ScoutManagement />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/scoutmanagement/sentreminders"
                              element={
                                <ProtectedRoute>
                                  <SentRemindersPage />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/scoutmanagement/templatelist"
                              element={
                                <ProtectedRoute>
                                  <ScoutTemplates />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/secretentry/templatelist"
                              element={
                                <ProtectedRoute>
                                  <ScoutSecretTemplates />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/scoutmanagement/newtemplate"
                              element={
                                <ProtectedRoute>
                                  <CreateTemplate />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/secretentry"
                              element={
                                <ProtectedRoute>
                                  <SecretEntry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/inprogress"
                              element={
                                <ProtectedRoute>
                                  <InProgress />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/settings"
                              element={
                                <ProtectedRoute>
                                  <Settings />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/chatroom"
                              element={
                                <ProtectedRoute>
                                  <ChatRoom />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/termsandpolicy"
                              element={
                                <ProtectedRoute>
                                  <Terms />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/company/order/complete/:gid/:rst/:ap/:god/:cod/:am/:tx/:sf/:ta/:submit/:tst"
                              element={
                                <ProtectedRoute>
                                  <OrderComplete />
                                </ProtectedRoute>
                              }
                            />

                            {/* Admin routes */}
                            <Route
                              path="/admin"
                              element={
                                <PublicRoute>
                                  <AdminLogin />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/admin/auth/admin-login"
                              element={
                                <PublicRoute>
                                  <AdminLogin />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/admin/auth/admin-forget-password"
                              element={
                                <PublicRoute>
                                  <AdminForgotPassword />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/admin/auth/admin-forget-password-mail"
                              element={
                                <PublicRoute>
                                  <AdminForgotPasswordMail />
                                </PublicRoute>
                              }
                            />
                            <Route
                              path="/admin/auth/admin-reset-password"
                              element={
                                <PublicRoute>
                                  <AdminResetPassword />
                                </PublicRoute>
                              }
                            />

                            <Route
                              path="/admin/dashboard"
                              element={
                                <ProtectedRoute>
                                  <AdminDashboard />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/notification/notifiy-bugs-features"
                              element={
                                <ProtectedRoute>
                                  <NotificationBugsFeatures />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/notification/notifiy-bugs-features-add"
                              element={
                                <ProtectedRoute>
                                  <NotificationBugsFeaturesAdd />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/notification/notification-alert"
                              element={
                                <ProtectedRoute>
                                  <NotificationAlert />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/agent/agent-list"
                              element={
                                <ProtectedRoute>
                                  <AgentList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/agent/agent-detail"
                              element={
                                <ProtectedRoute>
                                  <AgentDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/agent/add-agent"
                              element={
                                <ProtectedRoute>
                                  <AddAgent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/agent/edit-agent"
                              element={
                                <ProtectedRoute>
                                  <EditAgent />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/agent/suspended-agent-list"
                              element={
                                <ProtectedRoute>
                                  <SuspendedAgentList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-profile-list"
                              element={
                                <ProtectedRoute>
                                  <CompanyProfileList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-job-post-list"
                              element={
                                <ProtectedRoute>
                                  <CompanyJobPostList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-job-posting-list"
                              element={
                                <ProtectedRoute>
                                  <AdminCompanyJobPostingList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-job-posting-edit/:jobId"
                              element={
                                <ProtectedRoute>
                                  <AdminCompanyJobPostingEdit />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-deleted-jobs"
                              element={
                                <ProtectedRoute>
                                  <AdminCompanyDeletedJobs />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-scout-management"
                              element={
                                <ProtectedRoute>
                                  <CompanyScoutManagement />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-contract-billing-management"
                              element={
                                <ProtectedRoute>
                                  <CompanyContractBillingManagement />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-contract-billing-detail"
                              element={
                                <ProtectedRoute>
                                  <CompanyContractBillingDetail />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-inquiry-list"
                              element={
                                <ProtectedRoute>
                                  <CompanyInquiryList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-search-list"
                              element={
                                <ProtectedRoute>
                                  <CompanySearchList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-profile-detail"
                              element={
                                <ProtectedRoute>
                                  <CompanyProfileDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-job-post-detail"
                              element={
                                <ProtectedRoute>
                                  <CompanyJobPostDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-scout-log-detail"
                              element={
                                <ProtectedRoute>
                                  <CompanyScoutLogDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-scout-log-detail-resume"
                              element={
                                <ProtectedRoute>
                                  <CompanyScoutLogDetailsResume />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-withdrawn-list"
                              element={
                                <ProtectedRoute>
                                  <CompanyWithdrawnList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-withdrawn-companies"
                              element={
                                <ProtectedRoute>
                                  <WithdrawnCompanyList />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-job-consultation"
                              element={
                                <ProtectedRoute>
                                  <CandidateJobConsultation />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-job-consultation-log"
                              element={
                                <ProtectedRoute>
                                  <CandidateJobConsultationLog />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-enquiry"
                              element={
                                <ProtectedRoute>
                                  <CandidateEnquiry />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-job-consulation-detail"
                              element={
                                <ProtectedRoute>
                                  <CandidateJobConsultationDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-enquiry-conversation"
                              element={
                                <ProtectedRoute>
                                  <CandidateEnquiryConversation />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-agent-search"
                              element={
                                <ProtectedRoute>
                                  <CandidateAgentSearch />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-agent-search-details"
                              element={
                                <ProtectedRoute>
                                  <CandidateAgentSearchDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-job-consultation-before-person-assigned"
                              element={
                                <ProtectedRoute>
                                  <CandidateJobConsultationBeforePersonAssigned />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/candidate/candidate-job-consultation-proceed-interview"
                              element={
                                <ProtectedRoute>
                                  <CandidateJobConsultationProceedInterview />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/scout-generated-issue"
                              element={
                                <ProtectedRoute>
                                  <ScoutGeneratedIssue />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-contract-billing-log-detail"
                              element={
                                <ProtectedRoute>
                                  <CompanyContractLogDetails />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-contract-billing-log-detail-resume"
                              element={
                                <ProtectedRoute>
                                  <CompanyContractLogDetailsResume />
                                </ProtectedRoute>
                              }
                            />
                            <Route
                              path="/admin/company/company-contract-billing-log-details"
                              element={
                                <ProtectedRoute>
                                  <CompanyContractGeneratedDetail />
                                </ProtectedRoute>
                              }
                            />

                            {/* Catch-all Route for 404 */}
                            <Route path="*" element={<Navigate to="/company" replace />} />
                          </Routes>
                        </Suspense>
                      </div>
                    </AppLayout>
                  </UnreadCountCheckProvider>
                </SecretEntryProvider>
              </ScoutProvider>
            </AuthProvider>
          </CandidateWebSocketProvider>
        </CompanyWebSocketProvider>
      </AuthkeyCheckProvider>

      {/* The Generic Guard Modal (for most pages) */}
      <CompanyRegistrationGuard
        open={isRegistrationGuardOpen}
        onClose={() => setIsRegistrationGuardOpen(false)}
        onConfirm={() => setIsRegistrationGuardOpen(false)}
      />

      {/* The Step One Guard Modal (specifically for searchsettings) */}
      <CompanyStepOneRegistrationGuard
        open={isStepOneGuardOpen}
        onClose={() => setIsStepOneGuardOpen(false)}
        onConfirm={() => setIsStepOneGuardOpen(false)}
        onCompanyInfoClick={() => {
          /* navigation logic */
        }}
        onJobInfoClick={() => {
          /* navigation logic */
        }}
      />

      <CompanyStepTwoRegistrationGuard
        open={isStepTwoGuardOpen}
        onClose={() => setIsStepTwoGuardOpen(false)}
        onConfirm={() => setIsStepTwoGuardOpen(false)}
      />
    </>
  );
}

// =======================
//  App Wrapper
// =======================

function App() {
  useEffect(() => {
    // Hide scrollbar and keep the page scrollable
    document.body.style.overflow = 'auto'; // Enable scrolling
    document.body.style.scrollbarWidth = 'none'; // Hide scrollbar in Firefox
    document.body.style.msOverflowStyle = 'none'; // Hide scrollbar in IE and Edge

    // Add WebKit-specific rule for Chrome, Safari, and Opera
    const style = document.createElement('style');
    style.innerHTML = `
      body::-webkit-scrollbar {
        display: none; /* Hide scrollbar for Chrome, Safari, and Opera */
        height:'200vh';
      }
    `;
    document.head.appendChild(style);

    // Cleanup to restore default scrollbar behavior
    return () => {
      document.body.style.overflow = ''; // Reset to default
      document.body.style.scrollbarWidth = ''; // Reset Firefox style
      document.body.style.msOverflowStyle = ''; // Reset IE/Edge style
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  if (IS_SERVICE_SUSPENDED) {
     return <ServiceSuspended />;
  }

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;